import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, PhoneSchema, RecordIdSchema } from './identifiers';
import { MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Human resources — BMP-2 package 6 (`HR-EMP-001…003`, `HR-TIME-001`, `HR-TIME-002`,
 * `HR-CUSTODY-001`).
 *
 * The employee is its own aggregate, separate from the security account (ADR-0019): it may *refer* to
 * an account, never the other way round. Identity and personal contact data are restricted fields
 * (`hr.employee.viewSensitive`) and are stripped from every answer to someone without it.
 *
 * Nothing law-bearing is assumed (ADR-0034): no leave type exists until HR configures one (`BD-45`),
 * attendance is recorded and nothing is deducted (`BD-46`), and checklists are what HR writes.
 * Custody — assets in an employee's care (`FIN-ASSET`) and cashboxes they keep (`FIN-CASH`) — is read
 * from the owning modules, and an exit cannot be completed while any is outstanding.
 */

export const EMPLOYMENT_STATES = [
  'onboarding',
  'active',
  'suspended',
  'offboarding',
  'terminated',
] as const;
export const EmploymentStateSchema = z.enum(EMPLOYMENT_STATES);
export type EmploymentState = z.infer<typeof EmploymentStateSchema>;

export const CONTRACT_TYPES = ['indefinite', 'fixedTerm', 'partTime', 'consultancy'] as const;
export const EmploymentContractSchema = z.strictObject({
  contractNo: z.number().int().positive(),
  type: z.enum(CONTRACT_TYPES),
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema.optional(),
  probationEndsOn: BusinessDateSchema.optional(),
  jobTitle: z.string().optional(),
  note: z.string().optional(),
  recordedByAccountId: z.string(),
  recordedAt: InstantSchema,
});
export type EmploymentContract = z.infer<typeof EmploymentContractSchema>;

export const CHECKLIST_KINDS = ['onboarding', 'offboarding'] as const;
export const ChecklistItemSchema = z.strictObject({
  itemId: z.string(),
  kind: z.enum(CHECKLIST_KINDS),
  title: z.string(),
  done: z.boolean(),
  doneByAccountId: z.string().optional(),
  doneAt: InstantSchema.optional(),
  note: z.string().optional(),
});
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

export const EmployeeSensitiveSchema = z.strictObject({
  nationalId: z.string().optional(),
  birthDate: BusinessDateSchema.optional(),
  personalPhone: z.string().optional(),
  personalEmail: z.string().optional(),
  address: z.string().optional(),
  emergencyContact: z
    .strictObject({ name: z.string(), relation: z.string().optional(), phone: z.string() })
    .optional(),
});

export const EmployeeSchema = z.strictObject({
  employeeId: RecordIdSchema,
  number: z.string(),
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  jobTitle: z.string().optional(),
  managerEmployeeId: RecordIdSchema.optional(),
  /** Opaque reference to the security account this employee signs in with, if any. */
  accountId: z.string().optional(),
  /** The identifier the attendance device knows this employee by. */
  attendanceId: z.string().optional(),
  workPhone: z.string().optional(),
  workEmail: z.string().optional(),
  hireDate: BusinessDateSchema,
  endDate: BusinessDateSchema.optional(),
  state: EmploymentStateSchema,
  /** Present only for someone holding `hr.employee.viewSensitive`. */
  sensitive: EmployeeSensitiveSchema.optional(),
  contracts: z.array(EmploymentContractSchema),
  checklist: z.array(ChecklistItemSchema),
  exitReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Employee = z.infer<typeof EmployeeSchema>;

const SensitiveInput = z.strictObject({
  nationalId: z.string().trim().min(5).max(30).optional(),
  birthDate: BusinessDateSchema.optional(),
  personalPhone: PhoneSchema.optional(),
  personalEmail: z.string().trim().email().max(200).optional(),
  address: z.string().trim().min(3).max(300).optional(),
  emergencyContact: z
    .strictObject({
      name: EnteredNameSchema,
      relation: z.string().trim().min(2).max(60).optional(),
      phone: PhoneSchema,
    })
    .optional(),
});

export const CreateEmployeeSchema = z.strictObject({
  name: EnteredNameSchema,
  branchId: RecordIdSchema,
  departmentId: RecordIdSchema.optional(),
  jobTitle: z.string().trim().min(2).max(120).optional(),
  managerEmployeeId: RecordIdSchema.optional(),
  accountId: z.string().min(1).max(200).optional(),
  attendanceId: z.string().trim().min(1).max(60).optional(),
  workPhone: PhoneSchema.optional(),
  workEmail: z.string().trim().email().max(200).optional(),
  hireDate: BusinessDateSchema,
  sensitive: SensitiveInput.optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type CreateEmployee = z.infer<typeof CreateEmployeeSchema>;

export const UpdateEmployeeSchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  branchId: RecordIdSchema.optional(),
  departmentId: RecordIdSchema.nullable().optional(),
  jobTitle: z.string().trim().min(2).max(120).nullable().optional(),
  managerEmployeeId: RecordIdSchema.nullable().optional(),
  accountId: z.string().min(1).max(200).nullable().optional(),
  attendanceId: z.string().trim().min(1).max(60).nullable().optional(),
  workPhone: PhoneSchema.nullable().optional(),
  workEmail: z.string().trim().email().max(200).nullable().optional(),
  sensitive: SensitiveInput.optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const AddContractSchema = z
  .strictObject({
    type: z.enum(CONTRACT_TYPES),
    startsOn: BusinessDateSchema,
    endsOn: BusinessDateSchema.optional(),
    probationEndsOn: BusinessDateSchema.optional(),
    jobTitle: z.string().trim().min(2).max(120).optional(),
    note: z.string().trim().min(2).max(500).optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => !value.endsOn || value.endsOn >= value.startsOn, {
    message: 'END_BEFORE_START',
    path: ['endsOn'],
  })
  .refine((value) => value.type !== 'fixedTerm' || !!value.endsOn, {
    message: 'END_DATE_REQUIRED',
    path: ['endsOn'],
  });

export const ChecklistChangeSchema = z
  .strictObject({
    add: z
      .strictObject({ kind: z.enum(CHECKLIST_KINDS), title: z.string().trim().min(2).max(200) })
      .optional(),
    complete: z
      .strictObject({
        itemId: z.string().min(1).max(60),
        note: z.string().trim().max(300).optional(),
      })
      .optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => !!value.add !== !!value.complete, { message: 'ONE_CHANGE', path: ['add'] });

/** Lifecycle: activate (onboarding → active), suspend / reinstate, start exit, complete exit. */
export const EmployeeLifecycleSchema = z.strictObject({
  action: z.enum(['activate', 'suspend', 'reinstate', 'startExit', 'completeExit']),
  on: BusinessDateSchema,
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const EmployeeQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  branchId: RecordIdSchema.optional(),
  state: EmploymentStateSchema.optional(),
  search: z.string().trim().min(1).max(60).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type EmployeeQuery = z.infer<typeof EmployeeQuerySchema>;

/* -------------------------------------------------------------- custody */

export const CustodySchema = z.strictObject({
  employeeId: RecordIdSchema,
  assets: z.array(
    z.strictObject({
      assetId: z.string(),
      number: z.string(),
      name: z.string(),
      bookValue: MoneySchema,
    }),
  ),
  cashboxes: z.array(
    z.strictObject({
      accountId: z.string(),
      code: z.string(),
      name: z.strictObject({ ar: z.string(), en: z.string() }),
      balance: MoneySchema,
    }),
  ),
  /** True when nothing is in the employee's care: the exit may be completed. */
  clear: z.boolean(),
  /** False when the employee has no linked account, so custody cannot be read. */
  readable: z.boolean(),
});
export type Custody = z.infer<typeof CustodySchema>;

/* ----------------------------------------------------------- attendance */

export const ATTENDANCE_SOURCES = ['manual', 'import'] as const;
export const AttendanceRecordSchema = z.strictObject({
  recordId: RecordIdSchema,
  employeeId: RecordIdSchema,
  date: BusinessDateSchema,
  /** `HH:MM`, organization time. */
  checkIn: z.string().optional(),
  checkOut: z.string().optional(),
  source: z.enum(ATTENDANCE_SOURCES),
  note: z.string().optional(),
  correctedFrom: z
    .strictObject({ checkIn: z.string().optional(), checkOut: z.string().optional() })
    .optional(),
  recordedByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
});
export type AttendanceRecord = z.infer<typeof AttendanceRecordSchema>;

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'TIME_EXPECTED' });
export const RecordAttendanceSchema = z
  .strictObject({
    employeeId: RecordIdSchema,
    date: BusinessDateSchema,
    checkIn: Time.optional(),
    checkOut: Time.optional(),
    note: z.string().trim().min(2).max(300).optional(),
  })
  .refine((value) => !!value.checkIn || !!value.checkOut, {
    message: 'TIME_REQUIRED',
    path: ['checkIn'],
  })
  .refine((value) => !value.checkIn || !value.checkOut || value.checkOut > value.checkIn, {
    message: 'CHECKOUT_BEFORE_CHECKIN',
    path: ['checkOut'],
  });
export const CorrectAttendanceSchema = z
  .strictObject({
    checkIn: Time.optional(),
    checkOut: Time.optional(),
    note: z.string().trim().min(3).max(300),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => !value.checkIn || !value.checkOut || value.checkOut > value.checkIn, {
    message: 'CHECKOUT_BEFORE_CHECKIN',
    path: ['checkOut'],
  });
export const AttendanceQuerySchema = z.strictObject({
  employeeId: RecordIdSchema.optional(),
  branchId: RecordIdSchema.optional(),
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

/* ---------------------------------------------------------------- leave */

export const LeaveTypeSchema = z.strictObject({
  leaveTypeId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  paid: z.boolean(),
  /** Whether a request needs an available balance; unpaid leave usually does not. */
  requiresBalance: z.boolean(),
  state: z.enum(['active', 'inactive']),
  version: z.number().int().positive(),
});
export type LeaveType = z.infer<typeof LeaveTypeSchema>;
export const CreateLeaveTypeSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  paid: z.boolean(),
  requiresBalance: z.boolean(),
});

const Days = z.string().regex(/^\d{1,3}(\.5)?$/, { message: 'DAYS_EXPECTED' });

/** An entitlement granted by HR for a year (BD-45: nothing is granted by the system). */
export const GrantEntitlementSchema = z.strictObject({
  employeeId: RecordIdSchema,
  leaveTypeId: RecordIdSchema,
  year: z.number().int().min(2000).max(2100),
  days: Days,
  reason: z.string().trim().min(3).max(300),
  idempotencyKey: z.string().trim().min(8).max(200),
});

export const LeaveBalanceSchema = z.strictObject({
  leaveTypeId: RecordIdSchema,
  code: z.string(),
  name: z.string(),
  year: z.number().int(),
  entitled: z.string(),
  taken: z.string(),
  pending: z.string(),
  available: z.string(),
  requiresBalance: z.boolean(),
});
export type LeaveBalance = z.infer<typeof LeaveBalanceSchema>;

export const LEAVE_REQUEST_STATES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export const LeaveRequestSchema = z.strictObject({
  requestId: RecordIdSchema,
  employeeId: RecordIdSchema,
  leaveTypeId: RecordIdSchema,
  from: BusinessDateSchema,
  to: BusinessDateSchema,
  days: z.string(),
  reason: z.string().optional(),
  state: z.enum(LEAVE_REQUEST_STATES),
  requestedByAccountId: z.string(),
  decidedByAccountId: z.string().optional(),
  decisionNote: z.string().optional(),
  approvalRequestId: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
});
export type LeaveRequest = z.infer<typeof LeaveRequestSchema>;

export const RequestLeaveSchema = z
  .strictObject({
    employeeId: RecordIdSchema,
    leaveTypeId: RecordIdSchema,
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    /** Working days counted by the requester (calendars are BD-45); half days allowed. */
    days: Days,
    reason: z.string().trim().min(2).max(300).optional(),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .refine((value) => value.to >= value.from, { message: 'END_BEFORE_START', path: ['to'] })
  .refine((value) => value.from.slice(0, 4) === value.to.slice(0, 4), {
    message: 'ONE_YEAR_ONLY',
    path: ['to'],
  });
export const DecideLeaveSchema = z.strictObject({
  decision: z.enum(['approve', 'reject', 'cancel']),
  note: z.string().trim().min(2).max(300).optional(),
  expectedVersion: z.number().int().positive(),
});

export const HR_APPROVAL_OPERATIONS = {
  /** A leave request: decided in HR without a policy; through the engine where one governs it. */
  leave: 'hr.leave.approve',
} as const;

export const HR_AUDIT_ACTIONS = {
  employeeCreated: 'hr.employee.created',
  employeeUpdated: 'hr.employee.updated',
  employeeLifecycle: 'hr.employee.lifecycle',
  contractAdded: 'hr.employee.contractAdded',
  checklistChanged: 'hr.employee.checklistChanged',
  sensitiveViewed: 'hr.employee.sensitiveViewed',
  attendanceRecorded: 'hr.attendance.recorded',
  attendanceCorrected: 'hr.attendance.corrected',
  leaveTypeCreated: 'hr.leave.typeCreated',
  entitlementGranted: 'hr.leave.entitlementGranted',
  leaveRequested: 'hr.leave.requested',
  leaveDecided: 'hr.leave.decided',
} as const;
